import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Campaign } from '../../../../types';
import { campaignRepository } from '../../../../platform/firebase/campaignRepository';
import { fetchLlmUsage, LlmUsageRecord } from '../../../../services/llmUsage';
import { logGlobalError } from '../../../../utils/globalErrorLogger';
import { aggregateUsage, UsageSummary } from './aggregate';

export type Lookback = '7d' | '30d' | '90d' | 'all';

export const LOOKBACK_LABELS: Record<Lookback, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
};

const RECORD_LIMIT = 5000;

export interface TokenDashboardViewModel {
  busy: boolean;
  /** Set while a refetch is in flight (initial load also surfaces busy). */
  loading: boolean;
  error: string | null;
  campaigns: Campaign[];
  campaignId: string | null;
  setCampaignId: (id: string | null) => void;
  lookback: Lookback;
  setLookback: (l: Lookback) => void;
  /** Raw records driving the current view. Capped at RECORD_LIMIT. */
  records: LlmUsageRecord[];
  summary: UsageSummary;
  /** True when the query hit the row cap — totals are a lower bound. */
  truncated: boolean;
  recordLimit: number;
  initialize: () => Promise<void>;
  dispose: () => void;
  refresh: () => Promise<void>;
}

function lookbackToSince(lookback: Lookback): Date | null {
  if (lookback === 'all') return null;
  const days = lookback === '7d' ? 7 : lookback === '30d' ? 30 : 90;
  const since = new Date();
  since.setDate(since.getDate() - days);
  return since;
}

export function useTokenDashboardViewModel(): TokenDashboardViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignId, setCampaignIdState] = useState<string | null>(null);
  const [lookback, setLookbackState] = useState<Lookback>('30d');
  const [records, setRecords] = useState<LlmUsageRecord[]>([]);

  const unsubsRef = useRef<Array<() => void>>([]);
  // Generation counter so a slow in-flight fetch can't overwrite the state
  // after the user has switched filters.
  const fetchSeqRef = useRef<number>(0);

  const loadUsage = useCallback(
    async (selectedCampaignId: string | null, selectedLookback: Lookback) => {
      const seq = ++fetchSeqRef.current;
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchLlmUsage({
          campaignId: selectedCampaignId,
          since: lookbackToSince(selectedLookback),
          limit: RECORD_LIMIT,
        });
        if (seq !== fetchSeqRef.current) return;
        setRecords(rows);
      } catch (e: any) {
        if (seq !== fetchSeqRef.current) return;
        const msg = e?.message || 'Failed to load token usage';
        setError(msg);
        logGlobalError(e, {
          context: 'TokenDashboardTool.loadUsage',
          metadata: { campaignId: selectedCampaignId, lookback: selectedLookback },
        });
      } finally {
        if (seq === fetchSeqRef.current) setLoading(false);
      }
    },
    [],
  );

  const dispose = useCallback(() => {
    unsubsRef.current.forEach((u) => u());
    unsubsRef.current = [];
  }, []);

  const initialize = useCallback(async () => {
    setBusy(true);
    setError(null);
    dispose();
    const unsub = campaignRepository.subscribeAll(
      (next) => setCampaigns(next),
      (err) => setError(err.message),
    );
    unsubsRef.current = [unsub];
    try {
      await loadUsage(null, '30d');
    } finally {
      setBusy(false);
    }
  }, [dispose, loadUsage]);

  const setCampaignId = useCallback(
    (id: string | null) => {
      setCampaignIdState(id);
      void loadUsage(id, lookback);
    },
    [loadUsage, lookback],
  );
  const setLookback = useCallback(
    (l: Lookback) => {
      setLookbackState(l);
      void loadUsage(campaignId, l);
    },
    [loadUsage, campaignId],
  );

  const refresh = useCallback(async () => {
    await loadUsage(campaignId, lookback);
  }, [loadUsage, campaignId, lookback]);

  useEffect(() => () => dispose(), [dispose]);

  const summary = useMemo(() => aggregateUsage(records), [records]);
  const truncated = records.length >= RECORD_LIMIT;

  return {
    busy,
    loading,
    error,
    campaigns,
    campaignId,
    setCampaignId,
    lookback,
    setLookback,
    records,
    summary,
    truncated,
    recordLimit: RECORD_LIMIT,
    initialize,
    dispose,
    refresh,
  };
}
